import { useCallback, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

// Must match CURRENT_TERMS_VERSION in backend/app/database/users.py.
export const TERMS_VERSION = "v4";
const TERMS_URL = `/pdfs/terms-and-conditions.pdf?v=${TERMS_VERSION}`;
const END_SLACK_PX = 40;

const buttonSx = { borderRadius: 2, textTransform: "none", fontWeight: 600, px: 4, py: 1.5 };

// Clickwrap: the checkbox unlocks only after the reader scrolls to the last page, and
// Accept only after the box is ticked. The backend refuses an acceptance without both.
const TermsDialog = ({ open, onAccept, onCancel }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paneRef = useRef(null);
  const renderedRef = useRef(new Set());

  const measure = useCallback((node) => {
    paneRef.current = node;
    if (node) {setPageWidth(node.clientWidth - 32);}
  }, []);

  // The end only counts once every page is drawn, so placeholders can't be skipped past.
  // Also checked after each render: a pane that never overflows fires no scroll event.
  const checkEnd = () => {
    const el = paneRef.current;
    const allRendered = numPages > 0 && renderedRef.current.size === numPages;
    if (el && allRendered && el.scrollTop + el.clientHeight >= el.scrollHeight - END_SLACK_PX) {
      setScrolledToEnd(true);
    }
  };

  const handlePageRendered = (pageNumber) => {
    renderedRef.current.add(pageNumber);
    checkEnd();
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await onAccept({ version: TERMS_VERSION, scrolledToEnd, checkbox: checked });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      slotProps={{ backdrop: { sx: { backgroundColor: "transparent" } } }}
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
          Terms of Service and Master Services Agreement
        </Typography>
        <Typography variant="body2" sx={{ color: "error.dark", fontWeight: 500 }}>
          Required to continue
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          ref={measure}
          onScroll={checkEnd}
          data-testid="terms-scroll"
          sx={{ height: "60vh", overflowY: "auto", bgcolor: "grey.100", p: 2 }}
        >
          <Document
            file={TERMS_URL}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />}
            error={
              <Typography color="error" sx={{ p: 2 }}>
                The terms could not be loaded. Please refresh and try again.
              </Typography>
            }
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Box key={i} sx={{ mb: 2, boxShadow: 1 }}>
                <Page
                  pageNumber={i + 1}
                  width={pageWidth || undefined}
                  onRenderSuccess={() => handlePageRendered(i + 1)}
                />
              </Box>
            ))}
          </Document>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 3,
          py: 2,
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Label takes the free space and wraps inside it, so the buttons never move. */}
        <FormControlLabel
          sx={{ flex: 1, minWidth: 0, mr: 0 }}
          control={
            <Checkbox
              checked={checked}
              disabled={!scrolledToEnd}
              onChange={(e) => setChecked(e.target.checked)}
            />
          }
          label={
            scrolledToEnd
              ? "I have read and agree to the Terms of Service and Master Services Agreement"
              : "Scroll to the end of the document to continue"
          }
        />
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{ ...buttonSx, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            variant="contained"
            disabled={!scrolledToEnd || !checked || submitting}
            sx={buttonSx}
          >
            Accept and Continue
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TermsDialog;
